import SolutionsCaseStudyCTA from '@web/sections/Solutions/CaseStudyCTA'
import SolutionsChallenges from '@web/sections/Solutions/Challenges'
import SolutionsFooter from '@web/sections/Solutions/Footer'
import SolutionsHeader from '@web/sections/Solutions/Header'
import OtherSectors from '@web/sections/Solutions/OtherSectors'
import SolutionsRegulatoryContext from '@web/sections/Solutions/RegulatoryContext'
import SolutionsUseCases from '@web/sections/Solutions/UseCases'

export function AssetInfrastructurePlanningPage() {
  return (
    <>
      <SolutionsHeader
        header="Asset and infrastructure planning through Resilience Explorer®"
        arcadeLink={{
          src: 'https://demo.arcade.software/rwp0tcNflT8uxaFe2wqA?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
          title: 'How Resilience Explorer® Enables Adaptation Planning',
        }}
        subheader="Invest in the right assets, in the right places, at the right time."
        descriptionLeft={
          <>
            <p>
              Resilience Explorer® helps you understand how climate and natural hazard risks
              translate into asset damage, service disruption, and community impact. It gives you a
              consistent, current evidence base to support asset management plans, capital
              programmes, and long-term infrastructure strategies.
            </p>
          </>
        }
        descriptionRight={
          <p>
            The infrastructure decisions you make today will shape how communities function for
            decades. Getting them right, under genuine uncertainty about future climate and natural
            hazard conditions, requires more than age and condition data.
          </p>
        }
      />
      <SolutionsChallenges
        header="Challenges facing Asset and Infrastructure Planning"
        sections={[
          {
            title:
              'Planning infrastructure that must perform for decades as climate and natural hazard risks change',
            listitems: [],
          },
          {
            title:
              'Prioritising limited investment across a portfolio when risk information is fragmented or incomplete',
            listitems: [],
          },
          {
            title:
              'Demonstrating that investment decisions are supported by clear and credible risk evidence',
            listitems: [],
          },
        ]}
      />
      <SolutionsRegulatoryContext text="Infrastructure policy now expects councils and utilities to manage climate and natural hazard risk and maintain reliable services over the long term. Resilience Explorer® helps you do both." />
      <SolutionsUseCases
        header="How Resilience Explorer® Enables Asset and Infrastructure Planning"
        image={{
          src: '/website/asset_infrastructure_planning.jpg',
          alt: 'Asset and Infrastructure Planning Header',
        }}
        accordions={[
          {
            title: 'Identify assets most exposed to hazard risk',
            subtitle:
              'Understand which infrastructure assets face the greatest exposure across your network.',
            content: (
              <>
                <p>
                  Most organisations are still prioritising by age and condition alone, without a
                  clear picture of climate and natural hazard exposure across the portfolio.
                </p>
                <p className="mt-4">
                  Resilience Explorer® brings together hazard information and infrastructure data to
                  show which assets are exposed, to which hazards, and when first significant
                  exposure is expected, across your whole network and under future scenarios.
                </p>
                <p className="mt-4">
                  This means you can prioritise intelligently rather than by default, and show your
                  working when those priorities are questioned.
                </p>
              </>
            ),
          },
          {
            title: 'Understand how asset failure affects services',
            subtitle:
              'See how disruption to one asset can affect communities and service delivery across the network.',
            content: (
              <>
                <p>
                  Assets rarely fail in isolation, and the knock-on effects across transport,
                  electricity, water, and other services are often the hardest thing to anticipate.
                </p>
                <p className="mt-4">
                  Resilience Explorer® maps the dependencies across your network, identifying which
                  failures would have the widest consequences, not just the most obvious ones.
                </p>
                <p className="mt-4">
                  This means you can identify assets that look low-risk individually but are
                  critical to network continuity, and factor that into investment decisions.
                </p>
              </>
            ),
          },
          {
            title: 'Prioritise investment where it reduces risk most',
            subtitle:
              'Focus capital where it will have the greatest impact on service continuity and community outcomes.',
            content: (
              <>
                <p>
                  With constrained budgets and growing asset backlogs, every investment decision
                  carries real opportunity cost.
                </p>
                <p className="mt-4">
                  Resilience Explorer® identifies which assets and services are most critical to
                  maintaining community function during climate and natural hazard events.
                </p>
                <p className="mt-4">
                  This means you can direct upgrades, protection measures, and redundancy where they
                  will count most, and demonstrate that logic clearly to boards and funders.
                </p>
              </>
            ),
          },
          {
            title: 'Compare infrastructure investment options',
            subtitle: 'Understand how different investment choices influence future risk.',
            content: (
              <>
                <p>
                  Protect, strengthen, relocate, or accept some level of risk: these trade-offs are
                  difficult to make without comparable evidence across the options.
                </p>
                <p className="mt-4">
                  Resilience Explorer® lets you explore how different interventions influence hazard
                  exposure and service disruption over time.
                </p>
                <p className="mt-4">
                  This means your team can make the case for capital decisions with clear,
                  side-by-side evidence rather than competing assumptions.
                </p>
              </>
            ),
          },
          {
            title: 'Support asset management plans and capital programmes',
            subtitle:
              'Provide evidence that supports infrastructure planning and investment decisions.',
            content: (
              <>
                <p>
                  Boards, auditors, and regulators are asking harder questions about how climate and
                  natural hazard risk has been considered in long-term infrastructure planning.
                </p>
                <p className="mt-4">
                  Resilience Explorer® generates structured outputs that fit directly into asset
                  management plans, long-term infrastructure strategies, and capital works
                  programmes.
                </p>
                <p className="mt-4">
                  This means when those questions come, the evidence is traceable, current, and
                  ready.
                </p>
              </>
            ),
          },
        ]}
      />
      <SolutionsCaseStudyCTA header="Support infrastructure investment decisions with clear, current risk evidence." />
      <OtherSectors />
      <SolutionsFooter />
    </>
  )
}
